import {
  fetchUtils,
  DataProvider,
  GetListParams,
  CreateParams,
  UpdateParams,
  DeleteParams,
  GetOneParams,
  GetManyParams,
  GetManyReferenceParams,
  Identifier,
} from "react-admin";
import { addSearchMethod } from "@react-admin/ra-search";
import { AuthenticationResult } from "@azure/msal-browser";
import { getAuthProvider } from "../authProvider/authProviderInstance";
import { Claimant, ResourceName, ValidAny, normalizeTaskStatus } from "@/types";
import { USE_AUTH } from "@/config/auth";
import { isAllowedApiOrigin } from "@/config/apiOrigin";
import { NonRetryableClientError } from "@/config/retryPolicy";
import { trackEvent, trackError, setTag } from "@/utils/clarityTracking";
import { API_CONSTANTS, AUTH_CONSTANTS } from "@/constants";
import { createErrorAwareHttpClient, parseApiError, withFieldErrors } from "@aegle/ra-commons";
import { bindNormalizedFieldErrors } from "./fieldErrors";

// Get API URL from partner config or fall back to environment.
//
// The partner-specific apiBaseUrl is validated against an origin allowlist before
// being trusted: httpClient attaches the MSAL Bearer token unconditionally, so a
// poisoned PARTNER_CONFIG value could otherwise redirect authenticated calls to an
// attacker host. A failed/missing value falls back to the baked VITE_API_SERVER_URL
// rather than the untrusted value. See @/config/apiOrigin and ADO #5097.
export const getPartnerAwareApiUrl = (): string => {
  const envApiUrl = import.meta.env.VITE_API_SERVER_URL;
  try {
    const partnerConfig = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_CONFIG);
    if (partnerConfig) {
      const config = JSON.parse(partnerConfig);
      // Use partner-specific API URL only if its origin is allowlisted.
      if (config.apiBaseUrl) {
        if (isAllowedApiOrigin(config.apiBaseUrl)) {
          if (import.meta.env.DEV) console.log("[DataProvider] Using partner-specific API URL:", config.apiBaseUrl);
          return config.apiBaseUrl;
        }
        console.warn(
          "[DataProvider] Partner apiBaseUrl failed origin allowlist; falling back to environment URL:",
          config.apiBaseUrl
        );
      }
    }
  } catch (error) {
    console.warn("[DataProvider] Failed to get partner API URL:", error);
  }

  // Fall back to environment variable (missing config or rejected origin).
  if (import.meta.env.DEV) console.log("[DataProvider] Using environment API URL:", envApiUrl);
  return envApiUrl;
};

// Correlation ID management - generate new ID for each user action.
// Uses the native crypto.randomUUID() (all target browsers + Node 18+) rather than
// the `uuid` package, which has no other use here and whose ESM wrapper breaks under
// the Vitest transform.
let currentCorrelationId: string = crypto.randomUUID();

export const newCorrelationId = (): string => {
  currentCorrelationId = crypto.randomUUID();
  return currentCorrelationId;
};

export const getCorrelationId = (): string => currentCorrelationId;

// Helper to get partner ID from session storage
const getPartnerId = (): string | null => {
  try {
    const partnerConfig = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_CONFIG);
    if (partnerConfig) {
      const config = JSON.parse(partnerConfig);
      return config.partnerId || null;
    }
  } catch (error) {
    console.warn("[DataProvider] Failed to get partner ID from storage:", error);
  }
  return null;
};

/**
 * The partner id that should scope a request — the active partner override
 * (superadmin "acting as" a partner) if set, else the user's home partner.
 * Mirrors what httpClient sets as X-Partner-ID, so callers that bypass
 * httpClient (e.g. raw fetch for binary downloads) can scope correctly.
 */
export const getActivePartnerId = (): string | null =>
  sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.ACTIVE_PARTNER_ID) || getPartnerId();

// Add detailed logging
console.log("[DataProvider] Configuration:", {
  USE_AUTH,
  VITE_USE_AUTH: import.meta.env.VITE_USE_AUTH,
  defaultApiUrl: import.meta.env.VITE_API_SERVER_URL,
  authProvider: (window as ValidAny).authProvider ? "Present" : "Not found",
});

// Helper to build options and query params from meta
/**
 * Build a query string that properly handles array filter values.
 * Arrays become repeated params: `status=a&status=b` instead of `status=a,b`.
 */
const buildQueryString = (query: Record<string, unknown>): string => {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(key, String(item));
      }
    } else if (value !== null && value !== undefined) {
      params.append(key, String(value));
    }
  }
  return params.toString();
};

/**
 * Task status can arrive from the API in non-canonical casing. Normalise it to
 * the canonical `TaskStatus` enum as records enter the client so every picker and
 * comparison keys off a single source (ADO #5165 / #6310 AC3) — not by
 * de-duplicating option lists downstream. Covers the flat `tasks` resource and
 * nested `.../tasks` sub-collections (e.g. `users/{id}/tasks`).
 */
const isTasksResource = (resource: string): boolean =>
  resource === ResourceName.TASKS || resource.endsWith(`/${ResourceName.TASKS}`);

// Exported for unit testing — a pure, network-free helper, so tests assert the
// real normalization + tasks-guard rather than re-implementing the merge.
export const normalizeTaskRecord = (resource: string, record: ValidAny): ValidAny => {
  if (!isTasksResource(resource) || !record || typeof record !== "object") return record;
  if (typeof record.status !== "string") return record;
  return { ...record, status: normalizeTaskStatus(record.status) };
};

const normalizeTaskRecords = (resource: string, records: ValidAny[]): ValidAny[] =>
  isTasksResource(resource) ? records.map((r) => normalizeTaskRecord(resource, r)) : records;

const handleMetaExtension = (params: GetListParams, baseQuery: Record<string, unknown>) => {
  const query: Record<string, unknown> = { ...baseQuery };
  const options: fetchUtils.Options = {};

  if (params.meta?.expand) {
    query.expand = Array.isArray(params.meta.expand) ? params.meta.expand.join(",") : params.meta.expand;
  }

  const extraHeaders: Record<string, string> = {};

  if (params.meta?.["X-Fields"]) {
    extraHeaders["X-Fields"] = params.meta["X-Fields"];
  }

  if (params.meta?.includeDeleted) {
    extraHeaders["X-Include-Deleted"] = "true";
  }

  if (Object.keys(extraHeaders).length > 0) {
    options.headers = new Headers({
      Accept: "application/json",
      ...extraHeaders,
    });
  }

  return { query, options };
};

export const httpClient = async (url: string, options: fetchUtils.Options = {}) => {
  if (!options.headers) {
    options.headers = new Headers({ Accept: "application/json" });
  }

  console.log("[DataProvider] httpClient called for:", url);
  console.log("[DataProvider] USE_AUTH is:", USE_AUTH);
  console.log("[DataProvider] Request origin:", window.location.origin);
  console.log("[DataProvider] Target URL:", url);

  if (USE_AUTH) {
    try {
      const authProvider = getAuthProvider();
      console.log("[DataProvider] authProvider found:", !!authProvider);

      if (authProvider && authProvider.getAccessToken) {
        console.log("[DataProvider] Attempting to get access token...");
        const token = await authProvider.getAccessToken();

        if (token) {
          (options.headers as Headers).set("Authorization", `Bearer ${token}`);
          console.log("[DataProvider] Bearer token added to request");
        } else {
          console.warn("[DataProvider] No token returned from getAccessToken");
        }
      }
    } catch (error) {
      console.error("[DataProvider] Error getting access token:", error);
    }
  } else {
    console.log("[DataProvider] Auth disabled (VITE_USE_AUTH is false)");
  }

  // Add partner context headers
  // SuperAdmin partner override (from partner switcher) takes priority over resolved partner
  const activePartnerId = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.ACTIVE_PARTNER_ID);
  const partnerId = activePartnerId || getPartnerId();
  if (partnerId) {
    (options.headers as Headers).set(API_CONSTANTS.HEADERS.PARTNER_ID, partnerId);
    console.log("[DataProvider] X-Partner-ID header added:", partnerId);
  }

  // Add correlation ID header for request tracing
  (options.headers as Headers).set(API_CONSTANTS.HEADERS.CORRELATION_ID, getCorrelationId());

  // Track API calls and errors
  const startTime = Date.now();

  try {
    const response = await errorAwareFetchJson(url, options);

    // Track successful but slow API calls (optional - might be too noisy)
    // Uncomment if you want to track all API success
    const duration = Date.now() - startTime;
    if (duration > 5000) {
      // Only track slow calls
      trackEvent("api_slow_response");
      setTag("api_duration_ms", String(duration));
      setTag("api_url", url);
    }

    return response;
  } catch (error) {
    // Errors reaching here are already normalized by errorAwareFetchJson into an
    // HttpError whose body is an AegleApiError. Read the status/message off the
    // normalized shape for Clarity telemetry instead of regex-scraping a 3-digit
    // code out of the message string (the old `/(\d{3})/` hack).
    const parsed = parseApiError(error);
    const statusCode = parsed.status > 0 ? String(parsed.status) : "unknown";

    trackError("api_request", `status_${statusCode}`);
    setTag("api_url", url);
    setTag("error_message", parsed.message.substring(0, 100)); // Limit length

    // Re-throw to maintain normal error handling
    throw error;
  }
};

/**
 * `fetchUtils.fetchJson` wrapped so every rejection is normalized (via
 * `@aegle/ra-commons`) into an `HttpError` whose `body` is an `AegleApiError`
 * — a consistent `{ status, message, category, fields, correlationId }` shape
 * regardless of whether the failure came from the network, APIM, or mgmt-api.
 *
 * The inner `.catch` runs `promoteApiErrorBodyToMessage` FIRST, before
 * ra-commons normalizes: mgmt-api ships PascalCase `{Message}` / `{error}`
 * bodies, and ra-core's `fetchJson` leaves `HttpError.message` as the (blank on
 * HTTP/2, "Bad Request" on HTTP/1.1) statusText in that case. `parseApiError`
 * carries `HttpError.message` straight through for the Error branch, so without
 * this promotion the real API reason would be lost — re-introducing #4881.
 */
const baseErrorAwareFetchJson = createErrorAwareHttpClient((url: string, options?: fetchUtils.Options) =>
  fetchUtils.fetchJson(url, options).catch((err: unknown) => {
    promoteApiErrorBodyToMessage(err);
    throw err;
  })
);

/**
 * Error-aware fetchJson that additionally preserves the mgmt-api's structured
 * `details`/`code` on the normalized `AegleApiError` body — ra-commons keeps only
 * `message`/`category`/`fields`/`correlationId` and drops the rest (the untouched
 * backend JSON survives only under `.raw`). Re-attaching them here lets
 * `parseRichError` surface the actionable detail + support code in toasts
 * (#4265 "make errors more informative").
 */
const errorAwareFetchJson: typeof baseErrorAwareFetchJson = (url, options) =>
  baseErrorAwareFetchJson(url, options).catch((normalized: unknown) => {
    const body = (normalized as { body?: Record<string, unknown> })?.body;
    const rawBody = (body?.raw as { body?: Record<string, unknown> } | undefined)?.body;
    if (body && rawBody) {
      if (typeof rawBody.details === "string" && body.details === undefined) body.details = rawBody.details;
      if (typeof rawBody.code === "string" && body.code === undefined) body.code = rawBody.code;
    }
    throw normalized;
  });

/**
 * Mutates an error thrown by `fetchUtils.fetchJson` so its `message` reflects
 * the API's JSON error body (`{message}` / `{Message}` / `{error}`).
 *
 * ra-core's `fetchJson` constructs `new HttpError((json && json.message) || statusText, status, json)` —
 * so when the body is `{Message}` or `{error}` (mgmt-api's `apperror.APIError` shape on the
 * non-PascalCase keys), `message` falls back to `statusText`, which the HTTP/2 prod path leaves
 * blank ("Server communication error" toast) and HTTP/1.1 local proxy fills with "Bad Request".
 * We promote unconditionally rather than gating on an empty message because:
 *   - if `body.message` exists, ra-core has already used it → re-setting is a no-op
 *   - if only `body.Message`/`body.error` exists, the current message IS the statusText placeholder
 *     and the body string is the value we want
 *
 * Pulled out of `httpClient` so it can be unit-tested without faking the full fetch lifecycle.
 *
 * Note on plain-text bodies: bare `http.Error()` sites in mgmt-api ship plain text, which
 * `fetchJson` drops before constructing HttpError (body becomes `null`). Those cases land here
 * with no body content to promote — they are tracked under ADO #4881 AC4 (BE PR converts those
 * sites to `HandleErrorResponseWithCaller` so they ship JSON).
 *
 * Pattern mirrors `src/components/common/CreateInDialog.tsx` which already reads
 * `error.body.Message` / `error.body.message`.
 */
export const promoteApiErrorBodyToMessage = (error: unknown): void => {
  if (!error || typeof error !== "object" || !("body" in error)) return;
  const body = (error as { body?: unknown }).body;
  if (!body || typeof body !== "object") return;
  const b = body as { Message?: unknown; message?: unknown; error?: unknown };
  let resolved: string | undefined;
  if (typeof b.Message === "string") resolved = b.Message;
  else if (typeof b.message === "string") resolved = b.message;
  else if (typeof b.error === "string") resolved = b.error;
  if (resolved && error instanceof Error) {
    error.message = resolved;
  }
};

/**
 * One page of a KEYSET (cursor) paginated read.
 *
 * Distinct from react-admin's `{data, total}` because a cursor page cannot
 * answer "which page number am I on" — see getCursorList.
 */
export interface CursorPage<T> {
  data: T[];
  /** Server-side count of the WHOLE set, not of this page. */
  total: number;
  hasMore: boolean;
  /**
   * Pass as `from` to continue. Null on the last page.
   *
   * This is the next page's FIRST INDEX as the server observed it — never
   * `lastSeen + 1`. Record indices are not dense, so +1 can name a gap.
   */
  nextCursor: number | null;
}

export interface CursorListParams {
  /** Inclusive start index. 0 for the first page, `nextCursor` to continue. */
  from: number;
  limit: number;
  /** Extra query params, e.g. `{ target_schema: "claimant" }`. */
  filter?: Record<string, string | number | boolean | undefined>;
}

/**
 * Reads one page of a keyset-paginated endpoint (#6849).
 *
 * ## Why the provider needs this at all
 *
 * `getList` is page-number based: it turns `{page, perPage}` into
 * `?limit&offset`. Some endpoints deliberately reject `?offset` because on
 * their table it does not skip cheaply — measured on a 497,711-record
 * transformation, `OFFSET 497500` costs 195 ms and ~800 MB of I/O to return
 * 200 rows, against 0.12 ms for the equivalent keyset seek. Those endpoints
 * expose an inclusive cursor instead, which `page`/`perPage` cannot express.
 *
 * Before this existed, the one such consumer called `httpClient` directly and
 * carried a docblock explaining the deviation. That works exactly once; the
 * second such endpoint would copy the bespoke paging loop, and the
 * no-silently-truncated-lists guarantee would then live in two places. It
 * lives here.
 *
 * ## The guarantee this centralises
 *
 * A list that quietly stops short is the defect class behind #4478 (six weeks
 * of masked truncation). So a response claiming `has_more` while supplying no
 * cursor is treated as a PROTOCOL ERROR and throws, rather than being read as
 * "the end". Silently stopping is the failure mode that looks like success:
 * the caller renders a short list and nothing anywhere says it is incomplete.
 */
/**
 * Builds the query for a cursor page, validating the window.
 *
 * Separated from the fetch so the validation is unit-testable without faking
 * the fetch lifecycle — same reasoning as the update() payload transforms
 * (see tests/unit/providers/dataProvider.update.test.ts).
 *
 * A malformed window throws rather than being coerced. A `from` of NaN
 * stringifies to "NaN", which the server rejects with a 400 — but only after
 * a round trip, and the error surfaces as a generic request failure rather
 * than naming the caller's bug.
 */
export const buildCursorQuery = (params: CursorListParams): Record<string, string> => {
  if (!Number.isInteger(params.from) || params.from < 0) {
    throw new Error(`getCursorList: from must be a non-negative integer, received ${String(params.from)}`);
  }
  if (!Number.isInteger(params.limit) || params.limit <= 0) {
    throw new Error(`getCursorList: limit must be a positive integer, received ${String(params.limit)}`);
  }

  const query: Record<string, string> = {
    from_record_index: String(params.from),
    limit: String(params.limit),
  };
  for (const [key, value] of Object.entries(params.filter ?? {})) {
    if (value !== undefined && value !== "") query[key] = String(value);
  }
  return query;
};

/**
 * Unwraps the `{data, pagination}` envelope into a CursorPage.
 *
 * ## The guarantee this centralises
 *
 * A list that quietly stops short is the defect class behind #4478 (six weeks
 * of masked truncation). So a response claiming `has_more` while supplying no
 * cursor is a PROTOCOL ERROR and throws, rather than being read as "the end".
 * Silently stopping is the failure mode that looks like success: the caller
 * renders a short list and nothing anywhere says it is incomplete.
 *
 * The inverse is NOT an error. A cursor present with `has_more: false` is a
 * server that answered the question and volunteered a next position anyway;
 * `hasMore` is the authoritative field and the cursor is ignored.
 */
export const parseCursorPage = <T>(json: unknown, resource: string): CursorPage<T> => {
  const body = (json ?? {}) as {
    data?: T[];
    pagination?: { total?: number; has_more?: boolean; next_from_record_index?: number | null };
  };
  const pagination = body.pagination ?? {};
  const hasMore = pagination.has_more === true;
  const nextCursor = pagination.next_from_record_index ?? null;

  if (hasMore && nextCursor === null) {
    throw new Error(
      `getCursorList: ${resource} reported has_more with no next_from_record_index. ` +
        `Refusing to treat a truncated list as complete (#4478).`
    );
  }

  return {
    data: body.data ?? [],
    total: pagination.total ?? 0,
    hasMore,
    nextCursor: hasMore ? nextCursor : null,
  };
};

/**
 * Reads one page of a keyset-paginated endpoint (#6849).
 *
 * ## Why the provider needs this at all
 *
 * `getList` is page-number based: it turns `{page, perPage}` into
 * `?limit&offset`. Some endpoints deliberately reject `?offset` because on
 * their table it does not skip cheaply — measured on a 497,711-record
 * transformation, `OFFSET 497500` costs 195 ms and ~800 MB of I/O to return
 * 200 rows, against 0.12 ms for the equivalent keyset seek. Those endpoints
 * expose an inclusive cursor instead, which `page`/`perPage` cannot express.
 *
 * Before this existed, the one such consumer called `httpClient` directly and
 * carried a docblock explaining the deviation. That works exactly once; the
 * second such endpoint would copy the bespoke paging loop, and the
 * no-silently-truncated-lists guarantee would then live in two places.
 */
export const getCursorList = async <T>(resource: string, params: CursorListParams): Promise<CursorPage<T>> => {
  const query = buildCursorQuery(params);
  const url = `${getPartnerAwareApiUrl()}/${resource}?${buildQueryString(query)}`;
  const { json } = await httpClient(url);
  return parseCursorPage<T>(json, resource);
};

const baseDataProvider: DataProvider = {
  /**
   * Fetches a paginated list and returns `{ data, total }` for react-admin.
   *
   * Always pass an explicit `pagination.perPage` — the backend `getPaginationParams`
   * defaults `limit` to 10, which silently truncates result sets when no perPage is
   * supplied. Pick a value that matches the use case (selectors: 10–100; normal
   * lists: 100; "all of them" / exports / dashboards: 1000–10000).
   *
   * **Two URL shapes are supported:**
   *
   * 1. **Flat resource** (the common case) — `useGetList("policies", ...)` resolves
   *    to `GET /policies?limit=...&offset=...&sort=...`.
   *
   * 2. **Nested sub-collection** — pass a three-segment path
   *    `parent/{parentId}/child` and the URL is built as
   *    `GET /{parent}/{parentId}/{child}?limit=...&offset=...`. Use this for any
   *    sub-collection rooted under a parent record (e.g. a policy's
   *    aggregate-calculations, a client's notes, a claim's adjustments). This keeps
   *    list reads on the standard react-admin code path so consumers get envelope
   *    unwrapping, total-count handling, and cache invalidation for free instead of
   *    reimplementing them with raw `httpClient`.
   *
   *    Example:
   *    ```ts
   *    const { data, total, isLoading } = useGetList(
   *      `policies/${policyId}/aggregate-calculations`,
   *      {
   *        pagination: { page: 1, perPage: 1000 },
   *        sort: { field: "created_datetime", order: "DESC" },
   *      },
   *      { enabled: !!policyId },
   *    );
   *    ```
   *
   * Both shapes unwrap the backend `{data, pagination}` envelope and return
   * `{data, total}` to the caller. Only resort to direct `httpClient` calls for
   * action endpoints (POST `/calculate`, `/reconcile`, etc.) or single-record
   * reads — never for paginated list display.
   */
  getList: (resource: string, params: GetListParams) => {
    const page = params.pagination?.page || 1;
    const perPage = params.pagination?.perPage || 10;

    const baseQuery: Record<string, unknown> = {
      ...params.filter,
      limit: perPage.toString(),
      offset: ((page - 1) * perPage).toString(),
    };

    // Nested sub-collection path: parent/{parentId}/child
    const pathParts = resource.split("/");
    if (pathParts.length === 3) {
      const [parentResource, parentId, childResource] = pathParts;
      const { query, options } = handleMetaExtension(params, baseQuery);
      const url = `${getPartnerAwareApiUrl()}/${parentResource}/${parentId}/${childResource}?${buildQueryString(query)}`;

      return httpClient(url, options).then(({ json }) => {
        const rawData = Array.isArray(json) ? json : json.data || [];
        const total = Array.isArray(json) ? json.length : json.pagination?.total || json.data?.length || 0;

        // React-admin requires every record to expose a top-level `id`. Some
        // sub-collection endpoints return association objects whose identity
        // lives on a nested entity, which trips react-admin's dev-mode
        // response validation. Introduced for `users/{id}/roles` and
        // `roles/{id}/users` (role/user assignments). We backfill the id from
        // the nested entity; the `?? index` fallback is only stable within a
        // single response (a server-side sort-order change reassigns indexes),
        // which is acceptable for these read-only display lists. Extend the
        // nested-entity chain below if another assignment endpoint is added.
        const data = rawData.map((item: ValidAny, index: number) => {
          if (item && item.id !== null && item.id !== undefined) return item;
          const nested = item?.role ?? item?.user ?? item?.permission;
          return { ...item, id: nested?.id ?? index };
        });

        return { data: normalizeTaskRecords(resource, data), total };
      });
    }

    // Standard getList for top-level resources
    const field = params.sort?.field;
    const order = params.sort?.order;

    if (field) {
      baseQuery.sort = field;
      baseQuery.order = order || "ASC";
    }

    const { query, options } = handleMetaExtension(params, baseQuery);
    const url = `${getPartnerAwareApiUrl()}/${resource}?${buildQueryString(query)}`;

    return httpClient(url, options).then(({ json }) => {
      const rawData: Record<string, unknown>[] = json.data || [];
      const mapped = resource.startsWith("ref-codes/") ? rawData.map((item) => ({ ...item, id: item.code })) : rawData;
      return { data: normalizeTaskRecords(resource, mapped), total: json.pagination?.total || rawData.length || 0 };
    });
  },

  getOne: (resource: string, params: GetOneParams) => {
    // Guard against non-UUID IDs (e.g. "dashboard") that occur when React
    // Admin's internal routing resolves a CustomRoute path segment as a
    // resource record ID. Scoped to resources with dashboard CustomRoutes.
    const DASHBOARD_RESOURCES = ["policies", "stoploss-claims"];
    const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (DASHBOARD_RESOURCES.includes(resource) && params.id && !UUID_REGEX.test(String(params.id))) {
      // Reject with the pre-flight marker rather than a bare Error (#3804): the
      // retry policy recognizes it by identity, so a malformed ID is treated as
      // permanent instead of being retried 3× with backoff. Deliberately NOT a
      // synthetic `HttpError(…, 400)` — STD-008 §5 prohibits manual HttpError
      // construction here, and a fake 400 would be categorized as `validation`,
      // making a guard that never reached a validator look like a backend 422.
      return Promise.reject(new NonRetryableClientError(`Invalid resource ID: ${params.id}`));
    }

    const query: Record<string, string> = {};

    if (params.meta?.expand) {
      const expandValue = Array.isArray(params.meta.expand) ? params.meta.expand.join(",") : params.meta.expand;
      query.expand = expandValue;
    }

    const queryString = Object.keys(query).length ? `?${buildQueryString(query)}` : "";
    const url = `${getPartnerAwareApiUrl()}/${resource}/${params.id}${queryString}`;

    return httpClient(url).then(({ json }) => ({
      data: normalizeTaskRecord(resource, json),
    }));
  },

  getMany: (resource: string, params: GetManyParams) => {
    const requests = params.ids.map((id) =>
      httpClient(`${getPartnerAwareApiUrl()}/${resource}/${id}`)
        .then(({ json }) => json)
        .catch(() => null)
    );

    return Promise.all(requests).then((results) => ({
      data: normalizeTaskRecords(
        resource,
        results.filter((result) => result !== null)
      ),
    }));
  },

  getManyReference: (resource: string, params: GetManyReferenceParams) => {
    const page = params.pagination?.page || 1;
    const perPage = params.pagination?.perPage || 10;
    const field = params.sort?.field;
    const order = params.sort?.order;

    const query: Record<string, unknown> = {
      ...params.filter,
      [params.target]: params.id.toString(),
      limit: perPage.toString(),
      offset: ((page - 1) * perPage).toString(),
    };

    if (params.meta?.expand) {
      query.expand = Array.isArray(params.meta.expand) ? params.meta.expand.join(",") : params.meta.expand;
    }

    if (field) {
      query.sort = field;
      query.order = order || "ASC";
    }

    const url = `${getPartnerAwareApiUrl()}/${resource}?${buildQueryString(query)}`;

    return httpClient(url).then(({ json }) => ({
      data: normalizeTaskRecords(resource, json.data || []),
      total: json.pagination?.total || json.data?.length || 0,
    }));
  },

  create: (resource: string, params: CreateParams) => {
    // Track create operations
    trackEvent("resource_created");
    setTag("resource_type", resource);

    // Handle nested resources
    const pathParts = resource.split("/");
    if (pathParts.length === 3) {
      const [parentResource, parentId, childResource] = pathParts;

      return httpClient(`${getPartnerAwareApiUrl()}/${parentResource}/${parentId}/${childResource}`, {
        method: "POST",
        body: JSON.stringify(params.data),
      }).then(({ json }) => ({
        data: { ...params.data, id: json.id || json } as ValidAny,
      }));
    }

    return httpClient(`${getPartnerAwareApiUrl()}/${resource}`, {
      method: "POST",
      body: JSON.stringify(params.data),
    }).then(({ json }) => ({
      data: { ...params.data, id: json.id || json } as ValidAny,
    }));
  },

  update: (resource: string, params: UpdateParams) => {
    // Track update operations
    trackEvent("resource_updated");
    setTag("resource_type", resource);

    let data = { ...params.data };

    // Transform flattened claimant policy data back to nested structure
    if (resource === "claimants" && (data.status || data.enrollment || data.current_policy_data)) {
      const policyId = data._selectedPolicyId;

      if (policyId) {
        // Only transform if we have an explicit policy ID
        const existingPolicyData = data.policy_data?.[policyId] || {};
        data = {
          ...data,
          policy_data: {
            ...data.policy_data,
            [policyId]: {
              ...existingPolicyData,
              ...(data.current_policy_data || {}),
              ...(data.status ? { status: data.status } : {}),
              ...(data.enrollment ? { enrollment: data.enrollment } : {}),
            },
          },
        };
        delete data.status;
        delete data.enrollment;
        delete data.current_policy_data;
      } else {
        // No policy ID provided - log warning and strip flattened fields to avoid backend errors
        console.warn("[DataProvider] Claimant update has policy data but no _selectedPolicyId - skipping transform");
        delete data.status;
        delete data.enrollment;
        delete data.current_policy_data;
      }

      // Clean up internal field before sending to backend
      delete data._selectedPolicyId;
    }

    // Strip server-managed and response-only fields from ALL update payloads.
    // These are populated by the backend (audit trail, soft-delete, embedded joins)
    // and should never be sent back in PUT requests.
    const READONLY_FIELDS = [
      // Audit fields (server-managed)
      "created_by",
      "created_datetime",
      "modified_by",
      "modified_datetime",
      "deleted",
      "deleted_by",
      "deleted_datetime",
      // Embedded/expanded objects (populated via joins, not writable)
      "client",
      "claim_items",
      "policies",
      "source_documents",
      // Calculated aggregates (computed by backend for non-claim resources)
      "claim_count",
      "claimant_count",
      "amt_claim_submitted",
      "amt_reimbursement",
      "amt_adjusted",
      "amt_denied",
      "claim_items_count",
    ];

    for (const field of READONLY_FIELDS) {
      delete data[field];
    }
    // Claim money and dates: computed aggregates on policies/claimants, and on a
    // stop-loss claim owned by the engine. Either way they never ride along on an
    // update; the helper is what the unit test covers.
    stripStoplossClaimEngineFields(data);
    if (resource === "policies") {
      stripPolicyTerminationDate(data);
      stripPolicyEngineFields(data);
    }

    // Partners, claimants, and stoploss-claims use PATCH for sparse updates — the backend
    // PUT handler requires all fields and overwrites missing fields with zero values.
    // PATCH uses pointer fields with SQL COALESCE, leaving absent columns untouched.
    const method =
      resource === "partners" || resource === "claimants" || resource === "stoploss-claims" ? "PATCH" : "PUT";

    // Handle nested resources
    const pathParts = resource.split("/");
    if (pathParts.length >= 2) {
      return httpClient(`${getPartnerAwareApiUrl()}/${resource}/${params.id}`, {
        method,
        body: JSON.stringify(data),
      }).then(({ json }) => ({
        data: normalizeTaskRecord(resource, { ...params.data, id: params.id, ...(json ?? {}) }),
      }));
    }

    return httpClient(`${getPartnerAwareApiUrl()}/${resource}/${params.id}`, {
      method,
      body: JSON.stringify(data),
    }).then(({ json }) => ({
      data: normalizeTaskRecord(resource, { ...params.data, id: params.id, ...(json ?? {}) }),
    }));
  },
  updateMany: (resource: string, params: { ids: Identifier[]; data: ValidAny }) => {
    // Track bulk update operations
    trackEvent("resource_bulk_updated");
    setTag("resource_type", resource);
    setTag("bulk_count", String(params.ids.length));

    const requests = params.ids.map((id) =>
      httpClient(`${getPartnerAwareApiUrl()}/${resource}/${id}`, {
        method: "PUT",
        body: JSON.stringify(params.data),
      })
    );

    return Promise.all(requests).then(() => ({
      data: params.ids,
    }));
  },

  delete: (resource: string, params: DeleteParams) => {
    // Track delete operations
    trackEvent("resource_deleted");
    setTag("resource_type", resource);

    return httpClient(`${getPartnerAwareApiUrl()}/${resource}/${params.id}`, {
      method: "DELETE",
    }).then(() => ({
      data: params.previousData,
    }));
  },

  deleteMany: (resource: string, params: { ids: Identifier[] }) => {
    // Track bulk delete operations
    trackEvent("resource_bulk_deleted");
    setTag("resource_type", resource);
    setTag("bulk_count", String(params.ids.length));

    const requests = params.ids.map((id) =>
      httpClient(`${getPartnerAwareApiUrl()}/${resource}/${id}`, {
        method: "DELETE",
      })
    );

    return Promise.all(requests).then(() => ({
      data: params.ids,
    }));
  },

  // Generic invoke for action-style endpoints
  invoke: (resource: string, params?: { data?: ValidAny }) => {
    const { data } = params || {};

    return httpClient(`${getPartnerAwareApiUrl()}/${resource}`, {
      method: "POST",
      body: data ? JSON.stringify(data) : undefined,
    }).then(({ json }) => ({ data: json }));
  },

  // Record recovery: restore a single soft-deleted record
  restore: (resource: string, params: { id: Identifier }) => {
    trackEvent("resource_restored");
    setTag("resource_type", resource);

    return httpClient(`${getPartnerAwareApiUrl()}/${resource}/${params.id}/restore`, {
      method: "POST",
    }).then(({ json }) => ({ data: json }));
  },

  // Record recovery: restore multiple soft-deleted records
  restoreMany: (resource: string, params: { ids: Identifier[] }) => {
    trackEvent("resource_bulk_restored");
    setTag("resource_type", resource);
    setTag("bulk_count", String(params.ids.length));

    const requests = params.ids.map((id) =>
      httpClient(`${getPartnerAwareApiUrl()}/${resource}/${id}/restore`, {
        method: "POST",
      })
    );

    return Promise.all(requests).then(() => ({ data: params.ids }));
  },

  /**
   * Keyset-paginated read (#6849). Delegates to the exported `getCursorList`,
   * which is the typed entry point consumers should prefer — react-admin's
   * DataProvider index signature erases the generic here.
   */
  getCursorList: <T>(resource: string, params: CursorListParams) => getCursorList<T>(resource, params),

  // Record recovery: get count of soft-deleted records per resource
  getDeletedCounts: () => {
    return httpClient(`${getPartnerAwareApiUrl()}/admin/deleted-counts`).then(({ json }) => ({
      data: json,
    }));
  },
};

// Wrap the base data provider with search tracking
const dataProviderWithSearch = addSearchMethod(baseDataProvider, {
  users: {
    label: "full_name",
    redirect: "show",
  },
  clients: {
    label: "client_legal_name",
    redirect: "show",
  },
  partners: {
    label: "partner_company_name",
    redirect: "show",
  },
  policies: {
    label: "aegle_id",
    redirect: "show",
  },
  claimants: {
    label: (record) => {
      const claimant = record as Claimant | undefined;
      return claimant ? `${claimant.claimant_first_name} ${claimant.claimant_last_name}` : "";
    },
    redirect: "show",
  },
  "stoploss-claims": {
    label: "aegle_id",
    redirect: "show",
  },
  reimbursements: {
    label: "aegle_id",
    redirect: "show",
  },
  tasks: {
    label: "title",
    redirect: "show",
  },
  vendors: {
    label: "company_name",
    redirect: "show",
  },
});

// Wrap search method to track search events
const originalSearch = dataProviderWithSearch.search;
if (originalSearch) {
  dataProviderWithSearch.search = async (query: string) => {
    // Track search execution
    trackEvent("search_executed");
    setTag("search_query_length", String(query.length));
    setTag("search_has_content", query.trim().length > 0 ? "true" : "false");

    try {
      const result = await originalSearch(query);

      // Track search results
      const totalResults = result.data?.length || 0;
      trackEvent(totalResults === 0 ? "search_no_results" : "search_has_results");
      setTag("search_result_count", String(totalResults));

      // Track which resources were found
      if (totalResults > 0) {
        const resourceTypes = [...new Set(result.data?.map((item: ValidAny) => item.type))];
        setTag("search_result_types", resourceTypes.join(","));
      }

      return result;
    } catch (error) {
      trackError("search", "search_failed");
      throw error;
    }
  };
}

export const addRefreshAuthToDataProvider = <DataProviderType extends DataProvider = DataProvider>(
  provider: DataProviderType,
  refreshAuth: () => Promise<AuthenticationResult | void>
): DataProviderType => {
  return new Proxy(provider, {
    get(_, name) {
      return async (...args: ValidAny[]) => {
        await refreshAuth();
        return provider[name.toString()](...args);
      };
    },
  });
};

// Adopt ra-commons `withFieldErrors` (AC#6) for the raw-body path, then bind field
// errors from our normalized pipeline. `withFieldErrors` spreads the provider, so
// the custom search/invoke/restore/restoreMany/getDeletedCounts methods survive.
const dataProvider = bindNormalizedFieldErrors(withFieldErrors(dataProviderWithSearch));

export default dataProvider;

/**
 * #7320 / ADR-081 D5: the API couples `termination_date` to `status = cancelled`
 * and rejects the date on any other status. Policy updates are full PUTs of the
 * record, so a policy leaving "cancelled" would otherwise re-send its stale
 * termination date and get a 400. Drop it whenever the outgoing status is not
 * cancelled; when it is, the caller (status chip dialog / form) supplied the date.
 */
export const stripPolicyTerminationDate = (data: Record<string, unknown>): Record<string, unknown> => {
  if (data.status !== "cancelled") {
    delete data.termination_date;
  }
  return data;
};

/**
 * Stop-loss claim fields the engine (and Aura sync) own. The SPA edits a claim
 * by spreading the whole record and changing one field (notes, the repair
 * dialog's links), and the API's PATCH mapper persists every key it is sent, so
 * without this a notes save could write a stale total_paid_amount back over a
 * load that amended the claim moments earlier (#7629 review, !5022). On policies
 * and claimants the same names are read-only computed aggregates.
 */
export const ENGINE_OWNED_CLAIM_FIELDS = [
  "total_billed_amount",
  "total_paid_amount",
  "total_allowed_amount",
  "final_cumulative_total",
  "total_prior_submissions_ytd",
  "total_amount_accel_funded",
  "first_service_date",
  "last_service_date",
  "first_paid_date",
  "last_paid_date",
] as const;

export const stripStoplossClaimEngineFields = (data: Record<string, unknown>): Record<string, unknown> => {
  for (const field of ENGINE_OWNED_CLAIM_FIELDS) {
    delete data[field];
  }
  return data;
};

/**
 * #7387: `asd_tracking` is owned by the ASD engine (ADR-084), which rewrites it on
 * every run and carries the grow-only high-water mark that guards against double
 * submission. A policy save is a full PUT of the record as loaded, so echoing it
 * back would revert the engine's latest state. The API now ignores it on update
 * (coronis-apps !4899); this keeps the SPA from sending it at all.
 */
export const stripPolicyEngineFields = (data: Record<string, unknown>): Record<string, unknown> => {
  delete data.asd_tracking;
  return data;
};
